import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  CreditCard, AlertTriangle, ArrowLeft, Loader2, ExternalLink,
  Calendar, RefreshCw, CheckCircle2, MapPin, Sparkles, Crown
} from 'lucide-react';

const formatCurrency = (cents, currency = 'usd') =>
  cents == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

const formatDate = (epoch) =>
  !epoch ? '—' : new Date(epoch * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const monthsUntil = (month, year) => {
  if (!month || !year) return null;
  const exp = new Date(year, month - 1, 1);
  const now = new Date();
  return (exp.getFullYear() - now.getFullYear()) * 12 + (exp.getMonth() - now.getMonth());
};

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-amber-100 text-amber-700',
    canceled: 'bg-slate-200 text-slate-600',
    incomplete: 'bg-rose-100 text-rose-700',
    paid: 'bg-emerald-100 text-emerald-700',
    open: 'bg-amber-100 text-amber-700',
    void: 'bg-slate-100 text-slate-500',
  };
  return <Badge className={map[status] || 'bg-slate-100 text-slate-700'}>{status || 'none'}</Badge>;
};

const BillingCenter = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [meRes, plansRes] = await Promise.all([
        api.get('/billing/me'),
        api.get('/billing/plans'),
      ]);
      setBilling(meRes.data);
      setPlans(plansRes.data.plans || []);
    } catch (err) {
      toast.error('Failed to load billing details');
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post('/billing/portal', { origin: window.location.origin });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const subscribe = async (planKey) => {
    setSubscribing(planKey);
    try {
      const res = await api.post('/billing/subscribe', {
        plan_key: planKey,
        quantity: 1,
        origin: window.location.origin,
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
      </div>
    );
  }

  const hasBilling = billing?.has_billing && billing?.exists !== false;
  const card = billing?.card;
  const sub = billing?.subscription;
  const monthsLeft = card ? monthsUntil(card.exp_month, card.exp_year) : null;
  const cardExpiringSoon = monthsLeft !== null && monthsLeft <= 2;
  const cardExpired = monthsLeft !== null && monthsLeft < 0;

  // Filter plans by user audience
  const audience = user?.organization_id ? 'organization'
    : (user?.subscription_plan || '').startsWith('christian') ? 'christian'
    : 'parent';
  const visiblePlans = plans.filter((p) => p.audience === audience && p.type === 'flat');

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="billing-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-xl text-slate-800 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-500" /> Billing Center
              </h1>
              <p className="text-sm text-slate-500">Manage your subscription, payment method, and invoices</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="billing-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Card expiry warning */}
        {cardExpiringSoon && (
          <div
            className={`p-4 rounded-xl border flex items-start gap-3 ${cardExpired ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'}`}
            data-testid="card-expiring-warning"
          >
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${cardExpired ? 'text-rose-600' : 'text-amber-600'}`} />
            <div className="flex-1">
              <p className={`font-semibold ${cardExpired ? 'text-rose-900' : 'text-amber-900'}`}>
                {cardExpired ? 'Your card has expired' : `Your card expires in ${monthsLeft} month${monthsLeft === 1 ? '' : 's'}`}
              </p>
              <p className={`text-sm mt-0.5 ${cardExpired ? 'text-rose-700' : 'text-amber-700'}`}>
                {card.brand?.toUpperCase()} •••• {card.last4} — expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}.
                Update your payment method to avoid service interruption.
              </p>
              <Button
                size="sm"
                className={`mt-3 ${cardExpired ? 'bg-rose-600 hover:bg-rose-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
                onClick={openPortal}
                disabled={portalLoading}
                data-testid="warning-update-card"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update Card Now
              </Button>
            </div>
          </div>
        )}

        {/* No billing yet — show plan picker */}
        {!hasBilling && (
          <Card data-testid="no-billing-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-500" /> Choose a plan
              </CardTitle>
              <CardDescription>You don't have an active subscription yet. Pick a plan to get started.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {visiblePlans.length === 0 && (
                  <p className="text-slate-500 col-span-2">No plans available for your account type.</p>
                )}
                {visiblePlans.map((p) => (
                  <Card key={p.key} className="border-2 hover:border-emerald-400 transition-colors">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-bold text-lg text-slate-800">{p.name}</h3>
                        <Badge variant="outline" className="capitalize">{p.interval}ly</Badge>
                      </div>
                      <p className="text-3xl font-extrabold text-slate-900 mb-1">
                        ${(p.amount / 100).toFixed(2)}
                        <span className="text-sm font-normal text-slate-500">/{p.interval}</span>
                      </p>
                      <p className="text-sm text-slate-500 mb-4">{p.description}</p>
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => subscribe(p.key)}
                        disabled={subscribing === p.key}
                        data-testid={`subscribe-${p.key}`}
                      >
                        {subscribing === p.key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Subscribe
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {hasBilling && (
          <>
            {/* Subscription summary */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card data-testid="subscription-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-500" /> Current Subscription
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sub ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <StatusBadge status={sub.status} />
                        {sub.cancel_at_period_end && (
                          <Badge className="bg-rose-100 text-rose-700">Cancels at period end</Badge>
                        )}
                      </div>
                      <p className="text-3xl font-extrabold text-slate-900">
                        {formatCurrency(sub.amount, sub.currency)}
                        <span className="text-sm font-normal text-slate-500">/{sub.interval}</span>
                      </p>
                      <p className="text-sm text-slate-600 mt-2 flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" /> Next invoice on {formatDate(sub.current_period_end)}
                      </p>
                    </>
                  ) : (
                    <p className="text-slate-500">No active subscription.</p>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="payment-method-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-indigo-500" /> Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {card ? (
                    <>
                      <p className="font-mono text-lg uppercase text-slate-800">
                        {card.brand} •••• {card.last4}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={openPortal}
                        disabled={portalLoading}
                        data-testid="update-payment-method"
                      >
                        {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Update Payment Method
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-slate-500 mb-3">No card on file.</p>
                      <Button size="sm" onClick={openPortal} disabled={portalLoading} data-testid="add-payment-method">
                        {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Add Payment Method
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Address */}
            {billing.address && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-rose-500" /> Billing Address
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">
                    {[billing.address.line1, billing.address.line2, billing.address.city, billing.address.state, billing.address.postal_code, billing.address.country].filter(Boolean).join(', ')}
                  </p>
                  <Button size="sm" variant="ghost" className="mt-2 px-0" onClick={openPortal} disabled={portalLoading}>
                    Edit address →
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Manage button */}
            <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0">
              <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="font-bold text-lg">Need to make a change?</h3>
                  <p className="text-emerald-50 text-sm">Open the secure Stripe portal to update card, address, cancel, or download invoices.</p>
                </div>
                <Button
                  variant="outline"
                  className="bg-white text-emerald-700 hover:bg-emerald-50 border-0"
                  onClick={openPortal}
                  disabled={portalLoading}
                  data-testid="open-stripe-portal"
                >
                  {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Open Billing Portal
                </Button>
              </CardContent>
            </Card>

            {/* Invoices */}
            <Card data-testid="invoices-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Invoice History
                </CardTitle>
                <CardDescription>Last {billing.invoices?.length || 0} invoices</CardDescription>
              </CardHeader>
              <CardContent>
                {billing.invoices?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Number</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {billing.invoices.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="text-sm">{formatDate(inv.created)}</TableCell>
                          <TableCell className="text-sm font-mono">{inv.number || '—'}</TableCell>
                          <TableCell className="text-sm">{formatCurrency(inv.amount_paid || inv.amount_due, inv.currency)}</TableCell>
                          <TableCell><StatusBadge status={inv.status} /></TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {inv.hosted_invoice_url && (
                                <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline text-sm inline-flex items-center gap-1">
                                  View <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              {inv.invoice_pdf && (
                                <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:underline text-sm">
                                  PDF
                                </a>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-slate-500 text-sm">No invoices yet — your first one will appear here after your first billing cycle.</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default BillingCenter;
